"use client"

import { DEBUG, debugLog, debugGroup, debugGroupEnd, debugError } from '@/lib/debug'

// ============================================================
// Единый враппер запросов к Copilot API (зеркалит apiRequest из api-v3.ts)
// ============================================================

async function copilotRequest(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal,
): Promise<any> {
  const url = `${baseUrl}${path}`
  const method = (options.method || 'GET').toUpperCase()
  const headers = { 'Content-Type': 'application/json', ...options.headers }
  const endpoint = `${method} ${path}`

  if (DEBUG.shouldLog('copilot')) {
    debugGroup('copilot', `🚀 [Copilot] REQUEST: ${endpoint}`)
    debugLog('copilot', '📍 URL:', url)
    debugLog('copilot', '🔧 Method:', method)
    debugLog('copilot', '📋 Headers:', headers)
    if (options.body) {
      try { debugLog('copilot', '📄 Parsed Body:', JSON.parse(options.body as string)) }
      catch { debugLog('copilot', '📦 Body:', options.body) }
    }
    debugGroupEnd('copilot')
  }

  const response = await fetch(url, { ...options, headers, ...(signal ? { signal } : {}) })

  if (DEBUG.shouldLog('copilot')) {
    debugGroup('copilot', `📨 [Copilot] RESPONSE: ${endpoint} → ${response.status} ${response.statusText}`)
    debugLog('copilot', '📊 Status:', response.status, response.statusText)
    debugLog('copilot', '🔗 URL:', response.url)
    debugLog('copilot', '✅ OK:', response.ok)
    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => { responseHeaders[key] = value })
    debugLog('copilot', '📋 Response Headers:', responseHeaders)
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    let parsed: any = null
    try { parsed = JSON.parse(errorText) } catch { parsed = null }
    if (DEBUG.shouldLog('copilot')) {
      debugLog('copilot', '❌ Error Body (raw):', errorText)
      if (parsed !== null) debugLog('copilot', '❌ Error Data (parsed):', parsed)
      debugGroupEnd('copilot')
    }
    throw Object.assign(new Error(`[Copilot] ${endpoint} → ${response.status}`), { status: response.status, data: parsed })
  }

  const contentLength = response.headers.get('content-length')
  if (contentLength === '0') {
    if (DEBUG.shouldLog('copilot')) { debugLog('copilot', '✅ Response: Пустое тело'); debugGroupEnd('copilot') }
    return null
  }

  try {
    const data = await response.json()
    if (DEBUG.shouldLog('copilot')) { debugLog('copilot', '✅ Response Data:', data); debugGroupEnd('copilot') }
    return data
  } catch {
    const text = await response.text().catch(() => '')
    if (!text || text.trim() === '') {
      if (DEBUG.shouldLog('copilot')) { debugLog('copilot', '✅ Response: Пустое тело'); debugGroupEnd('copilot') }
      return null
    }
    if (DEBUG.shouldLog('copilot')) { debugLog('copilot', '⚠️ Не JSON, возвращаем текст:', text); debugGroupEnd('copilot') }
    return text
  }
}

// ============================================================
// Типы
// ============================================================

export type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } }

export type ChatMessage = {
  id?: string
  role: 'system' | 'user' | 'assistant'
  content: string | ContentPart[]
  timestamp?: number
}

export type StreamRequestBody = {
  model?: string
  messages: ChatMessage[]
  useWebSearch?: boolean
  useCodeInterpreter?: boolean
  reasoningEffort?: string
  reasoningSummary?: string
  verbosity?: string
}

export type StreamToolEvent = { name: string; status: 'start' | 'complete' }

export type ReadDocEvent = {
  status: 'reading' | 'result' | 'error'
  fileId: number
  fileName: string | null
  pages: number[]
  result?: object
  error?: string
}

export type StreamPayload = {
  delta?: string
  tool?: StreamToolEvent
  readDoc?: ReadDocEvent
  sources?: string[]
  usage?: { inputTokens?: number; outputTokens?: number }
  event?: 'done'
  error?: string
  reasoningDelta?: string
  reasoningDone?: boolean
  debug?: { type?: string; item?: any }
}

export type StreamCallbacks = {
  onDelta?: (delta: string) => void
  onTool?: (tool: StreamToolEvent) => void
  onReadDoc?: (readDoc: ReadDocEvent) => void
  onEvent?: (payload: StreamPayload) => void
  onDone?: (sources?: string[], startedAt?: number) => void
  onError?: (message: string) => void
}

export interface DialogItem {
  dialogId: string
  title: string
  createdAt: string
  updatedAt: string
  is_deleted: boolean
  /** Агент сейчас работает в этом диалоге (в фоне, даже если вкладка закрыта) */
  running?: boolean
  /** Папка проекта; null — чат в «Недавних» */
  projectId?: string | null
  /** Чат — ветка или копия другого чата */
  origin?: DialogOrigin | null
}

export interface DialogOrigin {
  kind: 'branch' | 'clone'
  dialogId: string
  title: string
  messageId?: string | null
}

export interface ChatProject {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

/** Чаты или папки проектов изменились (создан, скопирован, перемещён…) — списку пора обновиться */
export const DIALOGS_CHANGED_EVENT = 'casper:dialogs-changed'

export function notifyDialogsChanged(): void {
  window.dispatchEvent(new Event(DIALOGS_CHANGED_EVENT))
}

/** Агент начал или закончил работу в каком-то диалоге — списку чатов пора обновить индикаторы */
export const DIALOG_RUNS_CHANGED_EVENT = 'casper:dialog-runs-changed'

export interface CreateDialogResult {
  dialogId: string
  rootMessageId: string
  projectId?: string | null
}

export interface CopyDialogResult {
  dialogId: string
  title: string
  projectId: string | null
  origin: DialogOrigin | null
}

// ============================================================
// Определение URL Copilot API
// Правило:
//   production  → всегда NEXT_PUBLIC_COPILOT_API_URL
//   development → если NEXT_PUBLIC_USE_LOCAL_COPILOT=true, то NEXT_PUBLIC_LOCAL_COPILOT_API_URL
//                 иначе NEXT_PUBLIC_COPILOT_API_URL
// ============================================================

/** API теперь встроен в это же Next.js-приложение — всегда используем относительные пути. */
export const getCopilotApiUrl = (): string => ''

// ============================================================
// Подготовка сообщений (убирает картинки из всех, кроме последнего)
// ============================================================

export function prepareMessagesForSend(messages: ChatMessage[]): ChatMessage[] {
  const copy: ChatMessage[] = JSON.parse(JSON.stringify(messages))
  let lastImageIdx = -1
  for (let i = copy.length - 1; i >= 0; i--) {
    const m = copy[i]
    if (
      m?.role === 'user' &&
      Array.isArray(m.content) &&
      m.content.some((p: any) => p?.type === 'image_url')
    ) {
      lastImageIdx = i
      break
    }
  }
  for (let i = 0; i < copy.length; i++) {
    const m = copy[i]
    if (Array.isArray(m?.content)) {
      if (i !== lastImageIdx) {
        const filtered = (m.content as ContentPart[]).filter((p) => p.type !== 'image_url')
        if (filtered.length === 0) m.content = ''
        else if (filtered.length === 1 && filtered[0].type === 'text') m.content = filtered[0].text
        else m.content = filtered
      }
    }
  }
  return copy
}

// ============================================================
// Создать новый диалог
// ============================================================

export async function createDialog(
  token: string,
  workspaceId: number,
  projectId?: string | null,
): Promise<CreateDialogResult> {
  const data = await copilotRequest(getCopilotApiUrl(), '/api/dialog/create', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ workspaceId, ...(projectId ? { projectId } : {}) }),
  })
  return data as CreateDialogResult
}

// ============================================================
// Загрузить диалог по ID
// ============================================================

export async function loadDialog(
  dialogId: string,
  workspaceId: number,
  token: string,
  signal?: AbortSignal,
  format: 'compact' | 'full' = 'compact',
): Promise<any | null> {
  try {
    return await copilotRequest(
      getCopilotApiUrl(),
      `/api/dialog/${dialogId}?workspaceId=${workspaceId}&format=${format}`,
      { headers: { Authorization: `Bearer ${token}` } },
      signal,
    )
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e
    if (e?.status === 404) return null
    throw e
  }
}

// ============================================================
// Загрузить список диалогов
// ============================================================

export async function loadDialogs(
  workspaceId: number,
  token: string,
  signal?: AbortSignal,
): Promise<DialogItem[]> {
  try {
    const data = await copilotRequest(
      getCopilotApiUrl(),
      `/api/dialogs?workspaceId=${workspaceId}&includeDeleted=false`,
      { headers: { Authorization: `Bearer ${token}` } },
      signal,
    )
    return (data?.dialogs ?? []) as DialogItem[]
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e
    return []
  }
}

// ============================================================
// Удалить диалог
// ============================================================

export async function deleteDialog(
  dialogId: string,
  token: string,
): Promise<boolean> {
  try {
    await copilotRequest(getCopilotApiUrl(), `/api/dialog/${dialogId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    return true
  } catch {
    return false
  }
}

// ============================================================
// Название, ветка, копия, папка проекта
// ============================================================

const authJson = (token: string) => ({ Authorization: `Bearer ${token}` })

/** Текст ошибки API для уведомления: сообщение сервера или запасной текст */
export function copilotErrorMessage(e: unknown, fallback: string): string {
  const msg = (e as { data?: { error?: unknown } } | null)?.data?.error
  return typeof msg === 'string' && msg && !/^[a-z ]+$/i.test(msg) ? msg : fallback
}

export async function renameDialog(dialogId: string, title: string, token: string): Promise<string> {
  const data = await copilotRequest(getCopilotApiUrl(), `/api/dialog/${dialogId}/rename`, {
    method: 'PATCH',
    headers: authJson(token),
    body: JSON.stringify({ title }),
  })
  return (data?.title as string) ?? title
}

/** Новый чат с историей до сообщения messageId (id сообщения на сервере) */
export async function branchDialog(dialogId: string, messageId: string, token: string): Promise<CopyDialogResult> {
  return (await copilotRequest(getCopilotApiUrl(), `/api/dialog/${dialogId}/branch`, {
    method: 'POST',
    headers: authJson(token),
    body: JSON.stringify({ messageId }),
  })) as CopyDialogResult
}

export async function cloneDialog(dialogId: string, token: string): Promise<CopyDialogResult> {
  return (await copilotRequest(getCopilotApiUrl(), `/api/dialog/${dialogId}/clone`, {
    method: 'POST',
    headers: authJson(token),
  })) as CopyDialogResult
}

export async function moveDialogToProject(dialogId: string, projectId: string | null, token: string): Promise<void> {
  await copilotRequest(getCopilotApiUrl(), `/api/dialog/${dialogId}/project`, {
    method: 'PATCH',
    headers: authJson(token),
    body: JSON.stringify({ projectId }),
  })
}

export async function loadProjects(workspaceId: number, token: string, signal?: AbortSignal): Promise<ChatProject[]> {
  try {
    const data = await copilotRequest(getCopilotApiUrl(), `/api/chat-projects?workspaceId=${workspaceId}`, { headers: authJson(token) }, signal)
    return (data?.projects ?? []) as ChatProject[]
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e
    return []
  }
}

export async function createProject(workspaceId: number, name: string, token: string): Promise<ChatProject> {
  const data = await copilotRequest(getCopilotApiUrl(), '/api/chat-projects', {
    method: 'POST',
    headers: authJson(token),
    body: JSON.stringify({ workspaceId, name }),
  })
  return data.project as ChatProject
}

export async function renameProject(projectId: string, name: string, token: string): Promise<ChatProject> {
  const data = await copilotRequest(getCopilotApiUrl(), `/api/chat-projects/${projectId}`, {
    method: 'PATCH',
    headers: authJson(token),
    body: JSON.stringify({ name }),
  })
  return data.project as ChatProject
}

export async function deleteProject(projectId: string, token: string): Promise<void> {
  await copilotRequest(getCopilotApiUrl(), `/api/chat-projects/${projectId}`, {
    method: 'DELETE',
    headers: authJson(token),
  })
}

// ============================================================
// Стриминг чата (используется в lib/copilot-chat через streamChat)
// ============================================================

export function streamChat(
  body: StreamRequestBody,
  callbacks: StreamCallbacks = {}
) {
  const controller = new AbortController()
  const { onDelta, onTool, onReadDoc, onEvent, onDone, onError } = callbacks
  const startedAt = Date.now()

  const run = async () => {
    const { model: _model, ...requestBody } = body
    const streamUrl = '/api/chat/stream'

    const streamEndpoint = 'POST /api/chat/stream'

    if (DEBUG.shouldLog('copilot')) {
      debugGroup('copilot', `🚀 [Copilot] REQUEST: ${streamEndpoint}`)
      debugLog('copilot', '📍 URL:', streamUrl)
      debugLog('copilot', '📝 Messages count:', requestBody.messages?.length)
      requestBody.messages?.forEach((msg, i) => {
        debugLog('copilot', `  ${i + 1}. [${msg.role}]:`, typeof msg.content === 'string'
          ? msg.content.substring(0, 200) + (msg.content.length > 200 ? '...' : '')
          : `[${Array.isArray(msg.content) ? msg.content.length : 'unknown'} части]`
        )
      })
      debugLog('copilot', '⚙️ Options:', {
        useWebSearch: requestBody.useWebSearch,
        useCodeInterpreter: requestBody.useCodeInterpreter,
        reasoningEffort: requestBody.reasoningEffort,
      })
      debugGroupEnd('copilot')
    }

    const { getActiveAccount } = await import('@/lib/auth')
    const account = getActiveAccount()
    const bearerToken = account?.tokens?.accessToken ?? ''

    const requestHeaders: Record<string, string> = { 'Content-Type': 'application/json' }
    if (bearerToken) requestHeaders['Authorization'] = `Bearer ${bearerToken}`

    try {
      const resp = await fetch(streamUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      })

      if (DEBUG.shouldLog('copilot')) {
        debugGroup('copilot', `📨 [Copilot] RESPONSE: ${streamEndpoint} → ${resp.status} ${resp.statusText}`)
        debugLog('copilot', '📊 Status:', resp.status, resp.statusText)
        debugLog('copilot', '🔗 URL:', resp.url)
        debugLog('copilot', '✅ OK:', resp.ok)
        debugLog('copilot', 'ℹ️ (SSE stream — тело читается порциями ниже)')
        debugGroupEnd('copilot')
      }

      if (!resp.ok || !resp.body) {
        debugError('[Copilot] streamChat server error:', resp.status)
        onError?.('Сервер недоступен')
        onDone?.([], startedAt)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const collectedSources = new Set<string>()

      const extractUrls = (text: string) => {
        const urls: string[] = []
        if (!text) return urls
        const mdLinkRe = /\[[^\]]*\]\((https?:[^)\s]+)\)/gi
        let m: RegExpExecArray | null
        while ((m = mdLinkRe.exec(text)) !== null) urls.push(m[1])
        const bareRe = /(https?:\/\/[^\s)\]]+)/gi
        let b: RegExpExecArray | null
        while ((b = bareRe.exec(text)) !== null) urls.push(b[1])
        return urls
      }

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (controller.signal.aborted) break
        buffer += decoder.decode(value, { stream: true })
        let idx: number
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, idx)
          buffer = buffer.slice(idx + 2)
          const line = chunk.split('\n').find((l) => l.startsWith('data: '))
          if (!line) continue
          try {
            const payload: StreamPayload = JSON.parse(line.slice(6))
            onEvent?.(payload)

            if (process.env.NODE_ENV !== 'production') {
              const _uiLabel = (() => {
                if (payload.readDoc?.status === 'reading') {
                  const fn = payload.readDoc.fileName ?? `Документ #${payload.readDoc.fileId}`
                  const pp = (payload.readDoc.pages ?? []).join(', ')
                  return `📺 UI → Чтение документа «${fn}»${pp ? `, стр. ${pp}` : ''}`
                }
                if (payload.readDoc?.status === 'result') return '🔄 UI-clear → readDoc result'
                if (payload.readDoc?.status === 'error') return '🔄 UI-clear → readDoc error'
                if (payload.tool?.status === 'start') {
                  const lbl: Record<string, string> = { reasoning: 'Думаю...', web_search: 'Поиск в сети...', read_document: 'Чтение документа...', knowledge_base: 'Ищу в базе знаний...', code_interpreter: 'Выполнение кода...', file_search: 'Поиск в файлах...' }
                  return `📺 UI → ${lbl[payload.tool.name] ?? payload.tool.name}`
                }
                if (payload.tool?.status === 'complete') return '🔄 UI-clear → tool done'
                if (payload.event === 'done') return '✅ стрим завершён'
                return null
              })()
              if (!payload.delta && !payload.reasoningDelta) {
                const color = _uiLabel?.startsWith('📺') ? '#22c55e' : _uiLabel?.startsWith('🔄') ? '#f59e0b' : _uiLabel?.startsWith('✅') ? '#3b82f6' : '#9ca3af'
                const bold = _uiLabel?.startsWith('📺') ? 'bold' : 'normal'
                const key = Object.keys(payload).join(' + ')
                debugLog('copilot', `%c[SSE] ${key}`, `color:${color};font-weight:${bold}`, payload, _uiLabel ?? '')
              }
            }

            if (payload.delta) {
              onDelta?.(payload.delta)
              for (const u of extractUrls(payload.delta)) collectedSources.add(u)
            }
            if (payload.tool && payload.tool.name) {
              onTool?.(payload.tool)
              window.dispatchEvent(new CustomEvent('casper:tool-event', { detail: { tool: payload.tool } }))
            }
            if (payload.readDoc) {
              onReadDoc?.(payload.readDoc)
            }
            if (Array.isArray(payload.sources)) {
              for (const u of payload.sources) collectedSources.add(u)
            }
            if (payload.event === 'done') {
              debugLog('copilot', `✅ [Copilot] streamChat done, elapsed: ${Date.now() - startedAt}ms`)
              onDone?.(Array.from(collectedSources), startedAt)
            }
            if (payload.error) {
              debugError('[Copilot] streamChat payload error:', payload.error)
              onError?.(payload.error)
            }
          } catch {}
        }
      }

      if (!controller.signal.aborted) onDone?.(Array.from(collectedSources), startedAt)
    } catch (e: any) {
      if (!controller.signal.aborted) {
        debugError('[Copilot] streamChat network error:', e)
        onError?.(e?.message || 'Ошибка сети')
      }
      onDone?.([], startedAt)
    }
  }

  run()
  return controller
}
